import { TournamentResponse } from "../api/tournament";
import { TimerInfoState } from "../pages/timer-controller";
import { StructuresResponse } from "../pages/tournament/hook/useEditTournament";

export const useCopyTournamentText = () => {
  const formatToKoreanTime = (dateStr: string) => {
    if (!dateStr) return "";
    const timePart = dateStr.includes(" ") ? dateStr.split(" ")[1] : dateStr;
    const [hour, minute] = timePart.split(":").map(Number);
    const ampm = hour >= 12 ? "오후" : "오전";
    const displayHour = hour % 12 || 12;
    return `${ampm} ${displayHour}시 ${minute < 10 ? "0" + minute : minute}분`;
  };

  const copyTournamentText = async (
    data: TournamentResponse | null,
    timeInfo: TimerInfoState,
  ) => {
    if (!data) return;

    const saved = localStorage.getItem(`status-preset-${data.id}`);
    const template =
      saved && JSON.parse(saved).presetContent
        ? JSON.parse(saved).presetContent
        : `[메인타이틀]
[서브타이틀]

⏰ 게임 시작 : [시작시간]🕖
🎫 ENTRY CONDITION : [바이인금액]

현재 LV [현재레벨]    [스몰블라인드] / [빅블라인드] / [엔티]
⏰DURATION : [듀레이션]

🔹Live Players : [현재플레이어]
🔹Total Entry : [전체엔트리]

🔅 Starting : [바이인칩]
🔅 Re-entry : [리엔트리칩]

💠 레지마감 ➳ [레지마감레벨] LV ([레지마감시간])

🎫 RABBIT은 기명, 무가치이며 거래는 어떤 경우에도 불가능합니다.
RABBIT을 양도,양수할 경우 RB 몰수 및 러너펍 모든 서비스 이용 불가합니다.`;

    const startTime = formatToKoreanTime(data.startDate);
    const entryCondition = Math.floor((data.buyIn || 0) / 10000);

    const getDeadlineTime = (
      startDate: string,
      deadlineLevel: number,
      structure: StructuresResponse[],
    ) => {
      if (!startDate || !deadlineLevel || !structure) return "";

      const [datePart, timePart] = startDate.split(" ");
      const timeToUse = timePart || datePart;
      const [hour, minute] = timeToUse.split(":").map(Number);

      let startDateTime: Date;
      if (datePart && timePart) {
        const [year, month, day] = datePart.split("-").map(Number);
        startDateTime = new Date(year, month - 1, day, hour, minute);
      } else {
        startDateTime = new Date();
        startDateTime.setHours(hour, minute, 0, 0);
      }

      let totalMinutes = 0;
      const deadlineItem = structure.find(
        (s) => s.type === "LEVEL" && s.level === deadlineLevel,
      );
      const deadlineOrd = deadlineItem?.ord;

      if (!deadlineOrd) return "";

      const isNextBreak = structure[deadlineOrd]?.type === "BREAK";

      structure.forEach((s) => {
        if (s.ord <= deadlineOrd + (isNextBreak ? 1 : 0)) {
          totalMinutes += s.time;
        }
      });

      const deadlineDateTime = new Date(
        startDateTime.getTime() + totalMinutes * 60000,
      );

      const dHour = deadlineDateTime.getHours();
      const dMinute = deadlineDateTime.getMinutes();
      const ampm = dHour >= 12 ? "오후" : "오전";
      const displayHour = dHour % 12 || 12;

      return `${ampm} ${displayHour}시 ${dMinute < 10 ? "0" + dMinute : dMinute}분`;
    };

    const deadlineTime = getDeadlineTime(
      data.startDate,
      data.registerDeadlineLevel,
      data.structure,
    );

    const list: number[] = [];
    data.structure
      .filter((s) => s.type === "LEVEL")
      .forEach((s) => {
        if (list.length === 0 || list[list.length - 1] !== s.time) {
          list.push(s.time);
        }
      });
    const durationText = `${list.join("M / ")}M`;

    const livePlayers = data.userCount || 0;
    const totalEntries =
      (data.entryUserCount || 0) + (data.reBuyInUserCount || 0);

    const getCurrentLevelText = () => {
      if (timeInfo?.elapsedTimeMs && timeInfo?.elapsedTimeMs < 0)
        return "시작 준비중";
      if (timeInfo.currentLevel?.level) return timeInfo.currentLevel.level;
      if (timeInfo.currentLevel?.type === "BREAK") return "(BREAK)";
      return "종료된 게임";
    };

    const presetVariables = [
      { name: "[메인타이틀]", value: data.title },
      { name: "[서브타이틀]", value: data.subTitle || "" },
      { name: "[시작시간]", value: startTime },
      {
        name: "[바이인금액]",
        value: `${entryCondition.toLocaleString()} rabbit`,
      },
      { name: "[현재레벨]", value: getCurrentLevelText() },
      {
        name: "[스몰블라인드]",
        value: timeInfo.currentLevel?.smallBlind?.toLocaleString() || "0",
      },
      {
        name: "[빅블라인드]",
        value: timeInfo.currentLevel?.bigBlind?.toLocaleString() || "0",
      },
      {
        name: "[엔티]",
        value: timeInfo.currentLevel?.ante?.toLocaleString() || "0",
      },
      { name: "[현재플레이어]", value: livePlayers },
      { name: "[듀레이션]", value: durationText },
      { name: "[전체엔트리]", value: totalEntries.toLocaleString() },
      { name: "[바이인칩]", value: data.buyInChip?.toLocaleString() || "0" },
      {
        name: "[리엔트리칩]",
        value: data.reBuyInChip?.toLocaleString() || "0",
      },
      { name: "[레지마감레벨]", value: data.registerDeadlineLevel },
      { name: "[레지마감시간]", value: deadlineTime },
    ];

    let resultText = template;
    presetVariables.forEach((v) => {
      resultText = resultText.replaceAll(v.name, String(v.value));
    });

    try {
      await navigator.clipboard.writeText(resultText);
    } catch (err) {
      console.error("복사 실패:", err);
    }
  };

  return { copyTournamentText };
};
